
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { ModalInstance } from '~src/commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { removeQueryParamFromString } from '~src/commonInfra/services/WindowLocationService';
import { UrlParamKeys } from '~src/commonInfra/url/UrlParamKeys';
import { ITypedId } from '~src/httpClient/serverApi/types/ITypedId';
import { GrantAccessPermissionsHelper } from '~src/permissions/utils/GrantAccessPermissionsHelper';
import { sendGrantAccessEmailClickedTelemetry } from '~src/requestAccess/telemetry/RequestAccessTelemetry';
import { GrantPermissionsAfterSharingDialog } from '~src/sharing/components/namedSharing/GrantPermissionsAfterSharingDialog';
import { ExplorableItemType, ExplorableItemTypeValue } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { UserNames } from '~src/userManagement/resources/UserNames';
import { ISiteUser } from '~src/userManagement/users/serverApi/types/ISiteUser';
import { IUser } from '~src/userManagement/users/serverApi/types/IUser';

import { RequestAccessContent, RequestAccessDialog } from '../components/RequestAccessDialog';

export namespace RequestAccessAction {
  let dialogWithPreviewModalInstance: ModalInstance<unknown>;

  export function handleRequestAccess(requestAccessContent: RequestAccessContent, resultUrl: string) {
    RequestAccessDialog.open(requestAccessContent);
  }

  export async function handleGrantAccess(contentInfo: IContentInfo<ExplorableItemType>, resultUrl: string) {
    sendGrantAccessEmailClickedTelemetry(contentInfo.contentType);
    // aiAccess deep-links grant the `ai_access` capability instead of read. Workbook/view expose
    // `ai_access` directly; a project exposes it through its Workbooks permission template (the grant
    // fans out to all workbooks in the project). All other content types do not, so the variant is
    // gated behind `requestAccessForAIEnabled` — when the flag is off (or the content type is
    // unsupported) we degrade to the normal read grant.
    //
    // We additionally require the same two conditions the permissions grid uses to surface the
    // `ai_access` capability at all (see ViewCapabilities/WorkbookCapabilities in Capabilities.ts):
    // `analyticsAssistantDashboardQAEnabled` AND the site's `einsteinCopilotForTableauEnabled`. Without
    // both, `ai_access` is not a manageable capability on this site, so granting it here would write a
    // permission the recipient can neither see nor have revoked through the UI — degrade to read instead.
    const isAiAccess = isAiAccessGrantUrl(resultUrl)
      && FeatureFlags.requestAccessForAIEnabled
      && FeatureFlags.analyticsAssistantDashboardQAEnabled
      && SessionSiteSelectors.isEinsteinCopilotForTableauEnabled(getAppStore().getState())
      && (contentInfo.contentType === ExplorableItemTypeValue.workbook
        || contentInfo.contentType === ExplorableItemTypeValue.view
        || contentInfo.contentType === ExplorableItemTypeValue.project);
    const requestorId = getUserIdFromUrl(resultUrl);
    if (requestorId !== undefined && requestorId !== '') {
      const userInfo = await getUserInfo(requestorId);

      if (userInfo?.displayName !== null) {
        const permissionResult = await GrantAccessPermissionsHelper.canUserGrantPermission(contentInfo);
        if (permissionResult.canGrantPermissions) {
          var recipients = requestorId ? [{ recipientType: 'user', recipientId: Number(requestorId) }] : [];
          const usersWithoutPermissions: (IUser | ISiteUser)[] = isAiAccess
            ? await GrantAccessPermissionsHelper.getUsersWithoutAiAccessPermissionsForContent(recipients, contentInfo)
            : await GrantAccessPermissionsHelper.getUsersWithoutReadPermissionsForContent(recipients, contentInfo);
          // Verify that the user hasn't already been granted permissions
          if (usersWithoutPermissions.length > 0) {
            const unpermissionedRecipients: ITypedId[] = usersWithoutPermissions.map(user => {
              return { type: 'user', id: user.id };
            });
            GrantPermissionsAfterSharingDialog.open({ content: contentInfo, shareRecipients: recipients, unpermissionedRecipients, sourceAuthorizable: permissionResult.sourceAuthorizable, isRequestAccessDialogEnabled: true, requesterName: userInfo.displayName, requesterUsername: userInfo.username, isAiAccessGrant: isAiAccess });
          }
        }
      } else {
        ToasterService.instance.pop({
          type: ToastType.Warning,
          text: Messages.requestAccessUserRemoved(),
        });
      }
  }
  }

  const AI_ACCESS_GRANT_SEGMENT = 'grantAccess/aiAccess/users/';
  const READ_ACCESS_GRANT_SEGMENT = 'grantAccess/users/';

  /** True when the deep-link is the aiAccess grant variant (`.../grantAccess/aiAccess/users/<id>`). */
  export function isAiAccessGrantUrl(url: string): boolean {
    return url.indexOf(AI_ACCESS_GRANT_SEGMENT) !== -1;
  }

  export function getUserIdFromUrl(url: string) {
    // Match the aiAccess variant first; the read segment is not a contiguous substring of the
    // aiAccess URL (`aiAccess/` sits between `grantAccess/` and `users/`), so order matters.
    let grantAccessString = AI_ACCESS_GRANT_SEGMENT;
    let userIndex = url.lastIndexOf(grantAccessString);
    if (userIndex === -1) {
      grantAccessString = READ_ACCESS_GRANT_SEGMENT;
      userIndex = url.lastIndexOf(grantAccessString);
    }

    if (userIndex === -1) {
      return undefined; // No user identifier found
    }
    let userIdentifier = url.substring(userIndex + grantAccessString.length);

    // Check if the URL contains the isFromSaml parameter
    if (userIdentifier.includes(UrlParamKeys.IsFromSaml)) {
      // Remove the isFromSaml parameter
      userIdentifier = removeQueryParamFromString(userIdentifier, UrlParamKeys.IsFromSaml);
    }

    return userIdentifier;
  }

  export async function getUserInfo(id: string): Promise<{ displayName: string, username: string }> {
    const userInfo = await UserNames.getById(id);
    return userInfo === null ? null : { displayName: userInfo.displayName, username: userInfo.username };
  }
}

export type RequestAccessAction = typeof RequestAccessAction;